import React from 'react';
import SweetAlert from '../shared/SweetAlert';
import { connect } from 'react-redux';

import { AiRouter } from '../lib/utils';
import TileStateManager from '../lib/utils/TileStateManager.jsx';
import ActivityBoard from '../containers/activityBoard';
import TileCarousel from '../containers/tileCarousel';
import { InternalChatBot } from '../shared/InternalChatBot/InternalChatBot.jsx';
import { Fetcher } from '../lib/helpers';
import {
  setUserData,
  setOrganizationData,
  setDemoData,
  addTilesToStore,
  setTilesPerRow,
} from '../lib/redux/actions.jsx';
import {
  setTilesData,
  setTilesState,
} from '../lib/redux/tileStateManagerActions';
import { getSanitizedState } from '../lib/redux/selectors';
import routes from '../config/routes';
import calculateTileRowSize from '../lib/utils/calculateTileRowSize';

const Airbo = window.Airbo || {};

export class App extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      currentRoute: '',
      routeData: {},
      originId: null,
      tileOrigin: null,
      tileActions: null,
      appLoading: true,
      alert: null,
    };
    this.setUser = this.setUser.bind(this);
    this.setTiles = this.setTiles.bind(this);
    this.setTilesPaginationState = this.setTilesPaginationState.bind(this);
    this.addTiles = this.addTiles.bind(this);
    this.loadTiles = this.loadTiles.bind(this);
    this.openFullSizeTile = this.openFullSizeTile.bind(this);
    this.closeTile = this.closeTile.bind(this);
    this.redirectTo = this.redirectTo.bind(this);
    this.navigateTo = this.navigateTo.bind(this);
    this.setFlashMsg = this.setFlashMsg.bind(this);
    this.airouter = new AiRouter(routes, this);
    this.setTilesPerRow = this.setTilesPerRow.bind(this);
    this.loadTilesParams = this.loadTilesParams.bind(this);
  }

  componentDidMount() {
    this.airouter.connect();
    this.setInitialState();
    window.addEventListener('resize', this.setTilesPerRow);
  }

  componentWillUnmount() {
    this.airouter.disconnect();
    window.removeEventListener('resize', this.setTilesPerRow);
  }

  setTilesPerRow() {
    const rowSize = calculateTileRowSize(this.props.activeStatus);
    this.setState({ tilesPerRow: rowSize }, () => {
      this.props.setTilesPerRow(this.state.tilesPerRow);
    });
  }

  setInitialState() {
    const {
      loadedBoard,
      currentBoard,
      currentUser,
      isGuestUser,
    } = this.props.initData;

    const params = loadedBoard
      ? `demo_id=${currentBoard}&user_id=${currentUser}&is_guest_user=${isGuestUser}`
      : '';

    Fetcher.xmlHttpRequest({
      method: 'GET',
      path: `/api/v1/initialize?${params}`,
      success: response => {
        const resp = response.body;

        this.setUser(resp.user);
        this.setOrganization(resp.organization);
        this.setDemo(resp.demo);
        this.setState({ appLoading: false });
      },
      err: () => this.setState({ appLoading: false }),
    });
  }

  setFlashMsg(opts) {
    const {
      success,
      danger,
      warning,
      title,
      child,
      event,
      confirmBtnCssClass,
    } = opts;
    const alert = React.createElement(
      SweetAlert,
      {
        success,
        danger,
        warning,
        title,
        confirmBtnCssClass,
        onConfirm: () => {
          if (event) {
            Airbo.PubSub.publish(event);
          }

          this.setState({ alert: null });
        },
        style: {
          display: 'inherit',
          marginTop: '-250px',
          height: '370px',
          overflow: 'scroll',
        },
      },
      child,
    );
    this.setState({ alert });
  }

  setUser(data) {
    this.props.setUserData(data);
  }

  setTiles(data) {
    this.props.setTilesData(data);
  }

  setTilesPaginationState(data) {
    this.props.setTilesState(data);
  }

  setOrganization(data) {
    this.props.setOrganizationData(data);
  }

  setDemo(data) {
    this.props.setDemoData(data);
  }

  addTiles(data) {
    this.props.addTilesToStore(data);
  }

  redirectTo(path) {
    window.location = path;
  }

  navigateTo(path, opts) {
    if (opts && opts.flash) {
      this.setFlashMsg(opts.flash);
      if (this.props.demo.newAirbo) {
        const url = new URL(path, window.location.origin);
        const newPath = url.pathname;
        return this.airouter.navigation(newPath);
      }
    }

    return this.airouter.navigation(path);
  }

  openFullSizeTile(opts) {
    this.setState({
      originId: opts.id,
      tileOrigin: opts.from,
      tileActions: opts.tileActions,
    });
  }

  switchToTileOrigin = (newOrigin, newTileId) => {
    this.setState({
      tileOrigin: newOrigin,
      originId: newTileId,
    });
  };

  closeTile() {
    this.setState({ originId: null, tileOrigin: null, tileActions: null });
  }

  loadTilesParams(params) {
    const searchParams = new URLSearchParams();

    for (const [key, value] of Object.entries(params)) {
      if (value) {
        searchParams.append(key, value);
      }
    }

    return searchParams.toString();
  }

  loadTiles(opts) {
    const paginateState = {
      ...this.props.tiles.paginateState,
      ...(opts.paginateState || {}),
    };
    const {
      completeTilesPage,
      incompleteTilesPage,
      completeTilesOffset,
    } = paginateState;
    const { isGuestUser, id } = this.props.userData;

    const searchParams = {
      user_id: id,
      is_guest_user: isGuestUser,
      maximum_tiles: opts.perPage || 16,
      complete_tiles_page: completeTilesPage,
      incomplete_tiles_page: incompleteTilesPage,
      offset: completeTilesOffset,
    };

    if (opts.campaignUuid) {
      searchParams.campaign_uuid = opts.campaignUuid;
    }

    const params = this.loadTilesParams(searchParams);

    if (id) {
      Fetcher.xmlHttpRequest({
        method: 'GET',
        path: `/api/v1/tiles?${params}`,
        success: resp => {
          this.setTilesPaginationState({
            completeTilesPage: resp.completeTilesPage,
            incompleteTilesPage: resp.incompleteTilesPage,
            completeTilesOffset: resp.completeTilesOffset,
            allTilesDisplayed: resp.allTilesDisplayed,
            noTilesToDisplay: resp.noTilesToDisplay,
          });
          opts.success(resp);
        },
        err: resp => opts.error(resp),
      });
    }
  }

  render() {
    const {
      userData,
      tiles,
      organization,
      demo,
      progressBarData,
      initData,
    } = this.props;
    const loadRoute =
      this.state.currentRoute && !this.state.originId && !this.state.appLoading;
    let titleManagerDiv;
    if (this.state.originId !== null) {
      titleManagerDiv = React.createElement(TileStateManager, {
        originId: this.state.originId,
        tileOrigin: this.state.tileOrigin,
        tileActions: this.state.tileActions,
        closeTile: this.closeTile,
        navigateTo: this.navigateTo,
        addTiles: this.addTiles,
        loadTiles: this.loadTiles,
        switchToTileOrigin: this.switchToTileOrigin,
        userData,
        shouldConvertGuestAsUser: initData.shouldConvertGuestAsUser,
      });
    } else {
      titleManagerDiv = null;
    }
    const rootDiv = React.createElement(
      'div',
      { className: 'react-spa-root', key: 'root' },
      titleManagerDiv,
    );
    let routeDiv;
    if (loadRoute) {
      routeDiv = React.createElement(routes[this.state.currentRoute], {
        routeData: this.state.routeData,
        setUser: this.setUser,
        setTiles: this.setTiles,
        setTilesPaginationState: this.setTilesPaginationState,
        addTiles: this.addTiles,
        openFullSizeTile: this.openFullSizeTile,
        redirectTo: this.redirectTo,
        navigateTo: this.navigateTo,
        appLoading: this.state.appLoading,
        loadTiles: this.loadTiles,
        ctrl: initData,
        user: userData,
        key: 'route',
        tiles,
        demo,
        organization,
        progressBarData,
        tilesPerRow: this.state.tilesPerRow,
      });
    } else {
      routeDiv = '';
    }
    const routeComponent = routes[this.state.currentRoute];
    const isEmployeeView =
      this.state.originId !== null
        ? ['complete', 'incomplete'].includes(this.state.tileOrigin)
        : [ActivityBoard, TileCarousel].includes(routeComponent);
    const assistantUuid = demo.employeeWebAssistantEnabledUuid;
    const assistant =
      isEmployeeView && assistantUuid ? (
        <InternalChatBot key={assistantUuid} assistantUuid={assistantUuid} />
      ) : null;

    return [rootDiv, routeDiv, this.state.alert, assistant];
  }
}

// eslint-disable-next-line react-refresh/only-export-components
export default connect(getSanitizedState, {
  setUserData,
  setTilesData,
  setTilesState,
  setOrganizationData,
  setDemoData,
  addTilesToStore,
  setTilesPerRow,
})(App);
